import React, { FC } from 'react';

import { colors } from '../styles/tokens/colors';
import { useSiteMetadata } from '../hooks/useSiteMetadata';

export interface SeoDataProps {
  readonly metaAuthor?: string;
  readonly metaCopyright?: string;
  readonly h1?: string;
  readonly showOnSitemap: boolean | null;
  readonly remoteTypeName: string;
  readonly priority: number | null;
  readonly pageTitle: string | null;
  readonly pageDescription: string | null;
  readonly canonicalUrl: string | null;
  readonly allowCrawlersToIndex: boolean | null;
  readonly allowCrawlersToFollow: boolean | null;
}

export interface SeoProps {
  title?: string;
  meta?: any[];
  image?: string;
  schema?: any[];
  canonical?: string;
  data?: SeoDataProps | null;
}

export const SEO: FC<SeoProps> = ({
  data,
  meta = [],
  title,
  schema = [],
  image,
  canonical,
}) => {
  const {
    siteUrl,
    companyName,
    shortName,
    googleVerification,
    pinterestVerification,
    metaTitleFallback,
    twitter,
  } = useSiteMetadata();

  const inLanguage = 'en-GB';

  const companySchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Zemplerbank",
    "alternateName": "Cashplus",
    "url": "https://zemplerbank.com/",
    "logo": "https://zemplerbank.com/src/images/icon-192x192.png",
    "sameAs": [
      "https://www.cashplus.com/",
      "https://www.facebook.com/ZemplerBank/",
      "https://x.com/zemplerbank",
      "https://www.linkedin.com/company/zemplerbank"
    ]
  };

  const fullSchema = [companySchema, ...schema];

  const verification: any[] = [
    {
      name: 'google-site-verification',
      content: googleVerification,
    },
    {
      name: 'p:domain_verify',
      content: pinterestVerification,
    },
  ];

  const metaTitle = title || data?.pageTitle || metaTitleFallback;
  const metaDescription = data?.pageDescription ? data.pageDescription : '';
  const robotsIndex = data?.allowCrawlersToIndex ? 'index' : 'noIndex';
  const robotsFollow = data?.allowCrawlersToFollow ? 'follow' : 'nofollow';
  const brandColor = colors?.zemplerBlue|| null;
  const canonicalUrl = data?.canonicalUrl || null;

  const metaItems = [
    {
      name: `robots`,
      content: `max-snippet:-1, max-image-preview:large, max-video-preview:-1, ${robotsIndex}, ${robotsFollow}`,
    },
    {
      name: `description`,
      content: metaDescription.replace(/<br\s*[/]?>/gi, '\n'),
    },
    {
      name: `author`,
      content: data?.metaAuthor || null,
    },
    {
      name: `copyright`,
      content: data?.metaCopyright || null,
    },
    {
      name: `theme-color`,
      content: brandColor,
    },
    {
      name: `msapplication-TileColor`,
      content: brandColor,
    },
    {
      name: `msapplication-navbutton-color`,
      content: brandColor,
    },
    {
      name: `apple-mobile-web-app-status-bar-style`,
      content: brandColor,
    },
    {
      property: `og:site_name`,
      content: companyName,
    },
    {
      property: `og:title`,
      content: metaTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:locale`,
      content: inLanguage.replace('-', '_'),
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: 'og:image',
      content: image,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: twitter,
    },
    {
      name: `twitter:title`,
      content: data?.h1 || metaTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'twitter:image',
      content: image,
    },
  ]
    .filter((m) => !!m.content)
    .concat(meta, verification);

  return (
    <>
      <html lang={inLanguage} />
      <title>{metaTitle}</title>
      {metaItems?.map((metaItem, index) => (
        <meta
          name={metaItem?.name}
          content={metaItem?.content as string}
          property={metaItem?.property as string}
          key={index}
        />
      ))}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {fullSchema && (
        <script type="application/ld+json" id="json-schema">
          {JSON.stringify(
            fullSchema,
            undefined,
            process.env.NODE_ENV === 'development' ? 2 : 0
          )}
        </script>
      )}
    </>
  );
};
